import { Pipe, PipeTransform, OnDestroy } from "@angular/core";
import { IQBoostViewStore } from "@maximizer/ai/data-access";
import { AddressBookService } from "@maximizer/core/shared/data-access";
import { MenuOptionName } from '@maximizer/core/shared/ui/enums';
import { AiMarkupUtils } from '../classes/ai-markup-utils';
import { parseLooseMarkupProperties } from '../utils/markup-string-scan.util';
import { TranslateService } from "@ngx-translate/core";
import { Helper } from "../classes/iqboost";
import { ENTITY_KEY_PARAM, escapeHtmlAttribute } from "./ref-link.utils";

const CREATE_CRM_OBJECT_CLASS = 'create-crm-object';
const DATA_ATTRIBUTES = {
  KEY: 'data-key',
  ACTION: 'data-action',
  TITLE: 'data-title',
  DESCRIPTION: 'data-description',
  DATE: 'data-date',
  DISPLAY_TEXT: 'data-display-text'
} as const;

@Pipe({ name: 'createCRMObject', })
export class CreateCRMObjectPipe implements PipeTransform, OnDestroy {
  private clickHandler: ((event: MouseEvent) => void) | null = null;

  constructor(
    private readonly store: IQBoostViewStore,
    private readonly addressBook: AddressBookService,
    private readonly translate: TranslateService,
  ) {
    this.setupEventHandlers();
  }

  transform(value?: string): string {
    if (!value) return '';

    return AiMarkupUtils.replaceCRMObjects(value, (actionContent) =>
      this.handleCRMAction(actionContent)
    );
  }

  ngOnDestroy() {
    this.removeEventHandlers();
  }

  private setupEventHandlers() {
    this.removeEventHandlers();

    this.clickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target?.classList.contains(CREATE_CRM_OBJECT_CLASS)) {
        event.preventDefault();
        const action = target.getAttribute(DATA_ATTRIBUTES.ACTION);
        const entityKey = target.getAttribute(DATA_ATTRIBUTES.KEY) ?? '';
        
        if (action) {
          this.store.setConfirmCreateCRMAction({
            key: entityKey,
            displayText: target.getAttribute(DATA_ATTRIBUTES.DISPLAY_TEXT) ?? '',
            title: target.getAttribute(DATA_ATTRIBUTES.TITLE) ?? '',
            description: target.getAttribute(DATA_ATTRIBUTES.DESCRIPTION) ?? '',
            action: action as MenuOptionName,
            date: target.getAttribute(DATA_ATTRIBUTES.DATE) ?? '',
            openDialog: true,
          });

          if (entityKey) {
            this.addressBook.getDisplayValue(entityKey)
              .subscribe((displayValue) => 
                this.store.setConfirmCreateCRMActionFor(displayValue ?? '')
              );
          }
        }
      }
    };

    document.addEventListener('click', this.clickHandler);
  }

  private removeEventHandlers() {
    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler);
      this.clickHandler = null;
    }
  }

  private handleCRMAction(crmActionContent: string): string {
    try {
      const jsonObject = parseLooseMarkupProperties(crmActionContent);
      const actionType = jsonObject['type']?.toLowerCase() ?? '';
      const matchingAction = Helper.getCRMActionFromVariation(actionType);
      
      if (!matchingAction) return '';

      const displayText = this.translate.instant(matchingAction.result.actionTranslationKey);
      const entityKey = jsonObject[ENTITY_KEY_PARAM] ?? '';
      const title = jsonObject['title'] ?? '';
      const description = jsonObject['description'] ?? '';
      const date = jsonObject['date'] ?? '';
      const iconClass = `fa-icons icon-${matchingAction.result.icon} icon-16 mb-1 text-bush-green-140`;

      return `
        <button 
          kendobutton
          themecolor="secondary"
          class="${CREATE_CRM_OBJECT_CLASS} rounded k-button k-button-secondary leading-[0]"
          ${DATA_ATTRIBUTES.KEY}="${escapeHtmlAttribute(entityKey)}"
          ${DATA_ATTRIBUTES.ACTION}="${escapeHtmlAttribute(String(matchingAction.result.action))}"
          ${DATA_ATTRIBUTES.DISPLAY_TEXT}="${escapeHtmlAttribute(displayText)}"
          ${DATA_ATTRIBUTES.TITLE}="${escapeHtmlAttribute(title)}"
          ${DATA_ATTRIBUTES.DESCRIPTION}="${escapeHtmlAttribute(description)}"
          ${DATA_ATTRIBUTES.DATE}="${escapeHtmlAttribute(date)}"
          title="${escapeHtmlAttribute(title)}"
        >
          <e class="${iconClass} pointer-events-none"></e>
          <span class="text-bush-green-140 text-sm font-semibold pointer-events-none">${displayText}</span>
        </button>`;
    } catch (error) {
      console.error('Error handling CRM action:', error);
      return '';
    }
  }
}
